import { useState } from "react";
import { getBusinessId } from "../../database/helpers/business";
import moment from "moment";

export function useBusinessOverview({ business_id, navigate }) {
  const [profitAndLossOverview, setProfitAndLossOverview] = useState({
    total_expenses: null,
    profit: null,
    total_sales: null,
    pnl_loading: false,
  });

  const [cashFlowOverview, setCashFlowOverview] = useState({
    money_out: null,
    money_in: null,
    net_cash_flow: null,
    cf_loading: false,
  });

  const [stockOverview, setStockOverview] = useState({
    total_cost_value: null,
    total_items_in_stock: null,
    low_stock_items: null,
    stock_loading: false,
  });

  const [pnlStatement, setPnlStatement] = useState({
    net_profit: null,
    expenses_category: {},
    sales_category: {},
    total_business_sales: null,
    gross_profit: null,
    expenses: null,
    cost_of_sales: null,
    pnl_statement_loading: false,
  });

  const [cashflowStatement, setCashflowStatement] = useState({
    money_out_st: null,
    expenses_category_cf: {},
    net_cash_flow_st: null,
    money_in_st: null,
    sales_category_cf: {},
    cf_statement_loading: false,
  });

  const handleGetProfitAndLossOverview = async ({ startDate, endDate } = {}) => {
    try {
      setProfitAndLossOverview((prev) => ({ ...prev, pnl_loading: true }));

      const [businessRecord] = await getBusinessId(business_id);
      if (!businessRecord) return;

      const start = startDate ? moment(startDate).startOf("day").valueOf() : null;
      const end = endDate ? moment(endDate).endOf("day").valueOf() : null;

      const data = await businessRecord.getProfitAndLossStatement(start, end, { skipCategories: true });

      setProfitAndLossOverview((prev) => ({
        ...prev,
        total_sales: data.grossProfit ?? null,
        profit: data.netProfit ?? null,
        total_expenses: data.netExpenses ?? null,
      }));
    } catch (err) {
      console.error("PNL Overview Error:", err);
    } finally {
      setProfitAndLossOverview((prev) => ({ ...prev, pnl_loading: false }));
    }
  };

  const handleGetCashFLowOverview = async ({ startDate, endDate } = {}) => {
    try {
      setCashFlowOverview((prev) => ({ ...prev, cf_loading: true }));

      const [businessRecord] = await getBusinessId(business_id);
      if (!businessRecord) return;

      const start = startDate ? moment(startDate).startOf("day").valueOf() : null;
      const end = endDate ? moment(endDate).endOf("day").valueOf() : null;

      const data = await businessRecord.getCashFlowStatement(start, end, { skipCategories: true });

      setCashFlowOverview((prev) => ({
        ...prev,
        money_in: data.moneyIn ?? null,
        money_out: data.moneyOut ?? null,
        net_cash_flow: data.netCashFlow ?? null,
      }));
    } catch (err) {
      console.error("Cash Flow Overview Error:", err);
    } finally {
      setCashFlowOverview((prev) => ({ ...prev, cf_loading: false }));
    }
  };

  const handleGetStockOverview = async ({ startDate, endDate } = {}) => {
    try {
      setStockOverview((prev) => ({ ...prev, stock_loading: true }));

      const [businessRecord] = await getBusinessId(business_id);
      if (!businessRecord) return;

      const start = startDate ? moment(startDate).startOf("day").valueOf() : moment("20180101").startOf("day").valueOf();
      const end = endDate ? moment(endDate).endOf("day").valueOf() : moment().endOf("day").valueOf();

      const rawStocks = await businessRecord
        .getUnarchivedStockByDateAndSearchText(start, end)
        .unsafeFetchRaw();

      let totalCostValue = 0;
      let lowStockCount = 0;

      for (const item of rawStocks) {
        const stock = item.stock ?? 0;
        const price = item.cost_price ?? 0;

        if (stock >= 0) totalCostValue += stock * price;

        const threshold =
          item.low_stock !== null && item.low_stock !== undefined
            ? item.low_stock
            : 5;
        if (stock <= threshold) lowStockCount++;
      }

      setStockOverview((prev) => ({
        ...prev,
        total_cost_value: Math.round(totalCostValue * 100) / 100,
        total_items_in_stock: rawStocks.length,
        low_stock_items: lowStockCount,
      }));
    } catch (err) {
      console.error("Stock Overview Error:", err);
    } finally {
      setStockOverview((prev) => ({ ...prev, stock_loading: false }));
    }
  };

  const handleGetPNLStatement = async ({ startDate, endDate } = {}) => {
    try {
      setPnlStatement((prev) => ({
        ...prev,
        pnl_statement_loading: true,
      }));

      const [businessRecord] = await getBusinessId(business_id);
      if (!businessRecord) return;

      const start = startDate
        ? moment(startDate).startOf("day").valueOf()
        : null;
      const end = endDate ? moment(endDate).endOf("day").valueOf() : null;

      const data = await businessRecord.getProfitAndLossStatement(start, end);

      setPnlStatement((prev) => ({
        ...prev,
        net_profit: data.netProfit ?? null,
        expenses_category: data.expensesCategory ?? {},
        sales_category: data.salesCategory ?? {},
        total_business_sales: data.totalSales ?? null,
        gross_profit: data.grossProfit ?? null,
        expenses: data.netExpenses ?? null,
        cost_of_sales: data.costOfSales ?? null,
      }));
    } catch (err) {
      console.error("PNL Error:", err);
    } finally {
      setPnlStatement((prev) => ({
        ...prev,
        pnl_statement_loading: false,
      }));
    }
  };

  const handleGetCashflowStatement = async ({ startDate, endDate } = {}) => {
    try {
      setCashflowStatement((prev) => ({ ...prev, cf_statement_loading: true }));

      const [businessRecord] = await getBusinessId(business_id);
      if (!businessRecord) return;

      const start = startDate ? moment(startDate).startOf("day").valueOf() : null;
      const end = endDate ? moment(endDate).endOf("day").valueOf() : null;

      const data = await businessRecord.getCashFlowStatement(start, end);

      setCashflowStatement((prev) => ({
        ...prev,
        money_in_st: data.moneyIn ?? null,
        money_out_st: data.moneyOut ?? null,
        net_cash_flow_st: data.netCashFlow ?? null,
        sales_category_cf: data.salesCategory ?? {},
        expenses_category_cf: data.expensesCategory ?? {},
      }));
    } catch (err) {
      console.error("Cash Flow Statement Error:", err);
    } finally {
      setCashflowStatement((prev) => ({ ...prev, cf_statement_loading: false }));
    }
  };

  const { profit, total_sales, total_expenses, pnl_loading } =
    profitAndLossOverview;
  const { money_in, money_out, net_cash_flow, cf_loading } = cashFlowOverview;
  const {
    total_cost_value,
    total_items_in_stock,
    low_stock_items,
    stock_loading,
  } = stockOverview;

  const {
    net_profit,
    expenses_category,
    sales_category,
    total_business_sales,
    gross_profit,
    expenses,
    cost_of_sales,
    pnl_statement_loading,
  } = pnlStatement;

  const {
    money_out_st,
    expenses_category_cf,
    net_cash_flow_st,
    money_in_st,
    sales_category_cf,
    cf_statement_loading,
  } = cashflowStatement;

  return {
    handleGetProfitAndLossOverview,
    handleGetCashFLowOverview,
    handleGetStockOverview,
    handleGetPNLStatement,
    handleGetCashflowStatement,
    profit,
    total_expenses,
    total_sales,
    money_in,
    money_out,
    net_cash_flow,
    total_cost_value,
    total_items_in_stock,
    low_stock_items,
    net_profit,
    expenses_category,
    sales_category,
    total_business_sales,
    gross_profit,
    expenses,
    cost_of_sales,
    pnl_statement_loading,
    pnl_loading,
    cf_loading,
    stock_loading,
    money_out_st,
    expenses_category_cf,
    net_cash_flow_st,
    money_in_st,
    sales_category_cf,
    cf_statement_loading,
    money_out_st,
    expenses_category_cf,
    net_cash_flow_st,
    money_in_st,
    sales_category_cf,
    cf_statement_loading,
  };
}
